import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import EntHero from '../../components/enterprise/EntHero';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import EntConfirmDialog from '../../components/enterprise/EntConfirmDialog';
import { formatDate } from '../../utils/enterpriseUi';

function apiMessage(err, fallback) {
  return err.response?.data?.message || err.response?.data?.error || fallback;
}

function humanize(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ');
}

function emptyHolder() {
  return { name: '', title: '' };
}

function emptyShipping() {
  return {
    name: '',
    address1: '',
    address2: '',
    city: '',
    province: '',
    zip: '',
    country: 'United Kingdom',
    phone: '',
  };
}

export default function EnterpriseOrdersPage() {
  const { enterpriseId } = useParams();
  const { showSuccess, showError } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [cardKind, setCardKind] = useState('standard');
  const [quantity, setQuantity] = useState(1);
  const [holders, setHolders] = useState([emptyHolder()]);
  const [notes, setNotes] = useState('');
  const [primaryColour, setPrimaryColour] = useState('');
  const [shipping, setShipping] = useState(emptyShipping);
  const [bespoke, setBespoke] = useState(false);
  const [proofTarget, setProofTarget] = useState(null);
  const [proofDecision, setProofDecision] = useState('approved');
  const [proofNote, setProofNote] = useState('');
  const [proofBusy, setProofBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get('/api/enterprise/card-orders', { params: { enterpriseId } });
    setOrders(res.data.orders || []);
  }, [enterpriseId]);

  useEffect(() => {
    if (!enterpriseId) return undefined;
    let cancelled = false;
    setLoading(true);
    load()
      .catch((err) => {
        if (!cancelled) setError(apiMessage(err, 'Could not load orders'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, load]);

  useEffect(() => {
    const qty = Math.max(1, Number(quantity) || 1);
    setHolders((current) => {
      if (current.length === qty) return current;
      if (current.length < qty) {
        return [...current, ...Array.from({ length: qty - current.length }, emptyHolder)];
      }
      return current.slice(0, qty);
    });
  }, [quantity]);

  const updateHolder = (index, field, value) => {
    setHolders((current) => current.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const updateShipping = (field, value) => {
    setShipping((current) => ({ ...current, [field]: value }));
  };

  const submitOrder = async (event) => {
    event.preventDefault();
    const qty = Number(quantity);
    if (!Number.isInteger(qty) || qty < 1) {
      showError('Order at least one card.');
      return;
    }
    setSubmitting(true);
    setInvoiceUrl(null);
    try {
      const res = await api.post(
        '/api/enterprise/card-orders',
        {
          cardKind,
          quantity: qty,
          holders: holders
            .map((h) => ({ name: h.name.trim(), title: h.title.trim() }))
            .filter((h) => h.name || h.title),
          customisationSpec: {
            notes: notes.trim(),
            primaryColour: primaryColour.trim(),
          },
          shippingAddress: shipping,
          bespoke,
        },
        { params: { enterpriseId } }
      );
      const url = res.data?.shopify?.invoiceUrl || res.data?.cardOrder?.shopifyInvoiceUrl || res.data?.invoiceUrl;
      if (url) setInvoiceUrl(url);
      showSuccess(bespoke ? 'Quote request submitted.' : 'Order created.');
      setCardKind('standard');
      setQuantity(1);
      setHolders([emptyHolder()]);
      setNotes('');
      setPrimaryColour('');
      setBespoke(false);
      await load();
    } catch (err) {
      showError(apiMessage(err, 'Could not create that order'));
    } finally {
      setSubmitting(false);
    }
  };

  const openProof = (order, decision) => {
    setProofTarget(order);
    setProofDecision(decision);
    setProofNote('');
  };

  const confirmProof = async () => {
    if (!proofTarget) return;
    setProofBusy(true);
    try {
      await api.post(
        `/api/enterprise/card-orders/${proofTarget._id}/proof`,
        { decision: proofDecision, note: proofNote },
        { params: { enterpriseId } }
      );
      showSuccess(proofDecision === 'approved' ? 'Proof approved.' : 'Change request sent.');
      setProofTarget(null);
      await load();
    } catch (err) {
      showError(apiMessage(err, 'Could not update the proof'));
    } finally {
      setProofBusy(false);
    }
  };

  const invoiceFor = (order) => order.shopifyInvoiceUrl || order.invoiceUrl || null;

  const hero = (
    <EntHero
      title="Orders"
      lead="Order standard or custom cards for this company. Payment stays on Shopify when an invoice is created."
    />
  );

  const orderActions = (order) => (
    <>
      {invoiceFor(order) ? (
        <a className="ent-btn-analytics" href={invoiceFor(order)} target="_blank" rel="noreferrer">
          Shopify invoice
        </a>
      ) : null}
      {order.designStatus === 'proof_ready' ? (
        <>
          <button type="button" className="ent-btn-primary ent-btn-sm" onClick={() => openProof(order, 'approved')}>
            Approve
          </button>
          <button type="button" className="ent-btn-ghost ent-btn-sm" onClick={() => openProof(order, 'changes_requested')}>
            Request changes
          </button>
        </>
      ) : null}
    </>
  );

  if (loading) {
    return (
      <div className="ent-content">
        {hero}
        <EntSkeleton variant="block" />
        <EntSkeleton variant="rows" count={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ent-content">
        {hero}
        <div className="ent-flash ent-flash-error" role="alert">{error}</div>
      </div>
    );
  }

  return (
    <div className="ent-content">
      {hero}

      <section className="ent-section">
        <h2 className="ent-section-title">New order</h2>
        <form className="ent-panel" onSubmit={submitOrder}>
          <div className="ent-form-grid">
            <div className="ent-form-group">
              <label htmlFor="order-kind">Card</label>
              <select
                id="order-kind"
                className="ent-select"
                value={cardKind}
                onChange={(e) => setCardKind(e.target.value)}
              >
                <option value="standard">Standard</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className="ent-form-group">
              <label htmlFor="order-qty">Quantity</label>
              <input
                id="order-qty"
                className="ent-input"
                type="number"
                min={1}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="ent-form-group">
              <label htmlFor="order-colour">Colour note</label>
              <input
                id="order-colour"
                className="ent-input"
                value={primaryColour}
                onChange={(e) => setPrimaryColour(e.target.value)}
                placeholder="#C6A75E"
              />
            </div>
          </div>

          <div className="ent-form-group">
            <label htmlFor="order-notes">Logo and colour notes</label>
            <textarea
              id="order-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Logo placement, colour, finish…"
            />
          </div>

          <h3 className="ent-section-title">Holders (optional)</h3>
          {holders.map((holder, index) => (
            <div key={index} className="ent-form-grid">
              <div className="ent-form-group">
                <label htmlFor={`holder-name-${index}`}>Name</label>
                <input
                  id={`holder-name-${index}`}
                  className="ent-input"
                  value={holder.name}
                  onChange={(e) => updateHolder(index, 'name', e.target.value)}
                />
              </div>
              <div className="ent-form-group">
                <label htmlFor={`holder-title-${index}`}>Title</label>
                <input
                  id={`holder-title-${index}`}
                  className="ent-input"
                  value={holder.title}
                  onChange={(e) => updateHolder(index, 'title', e.target.value)}
                />
              </div>
            </div>
          ))}

          <h3 className="ent-section-title">Shipping</h3>
          <div className="ent-form-grid">
            {[
              ['name', 'Recipient'],
              ['address1', 'Address'],
              ['address2', 'Address line 2'],
              ['city', 'City'],
              ['province', 'Region'],
              ['zip', 'Postcode'],
              ['country', 'Country'],
              ['phone', 'Phone'],
            ].map(([field, label]) => (
              <div className="ent-form-group" key={field}>
                <label htmlFor={`ship-${field}`}>{label}</label>
                <input
                  id={`ship-${field}`}
                  className="ent-input"
                  value={shipping[field]}
                  onChange={(e) => updateShipping(field, e.target.value)}
                />
              </div>
            ))}
          </div>

          <label className="ent-checkbox-label">
            <input type="checkbox" checked={bespoke} onChange={(e) => setBespoke(e.target.checked)} />
            Request a bespoke quote
          </label>

          {invoiceUrl ? (
            <p className="ent-flash ent-flash-success" role="status">
              Shopify invoice ready.{' '}
              <a href={invoiceUrl} target="_blank" rel="noreferrer">Open invoice</a>
            </p>
          ) : null}

          <button type="submit" className="ent-btn-primary" disabled={submitting} style={{ marginTop: 16 }}>
            {submitting ? 'Submitting…' : bespoke ? 'Request quote' : 'Place order'}
          </button>
        </form>
      </section>

      <section className="ent-section">
        <h2 className="ent-section-title">Orders</h2>
        {orders.length === 0 ? (
          <EntEmptyState title="No card orders yet" lead="Place an order above. Invoices and proofs will appear here." />
        ) : (
          <>
            <div className="ent-table-wrap ent-hide-sm">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Card</th>
                    <th>Qty</th>
                    <th>Payment</th>
                    <th>Design</th>
                    <th>Production</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order._id}>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>{order.bespoke || order.orderType === 'bespoke_quote' ? 'Bespoke' : humanize(order.cardKind)}</td>
                      <td>{order.quantity}</td>
                      <td>{humanize(order.paymentStatus)}</td>
                      <td>{humanize(order.designStatus)}</td>
                      <td>{humanize(order.productionStatus)}</td>
                      <td className="ent-actions-cell">{orderActions(order)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ent-stack">
              {orders.map((order) => (
                <div key={order._id} className="ent-stack-card ent-stack-card-static">
                  <strong>{order.bespoke || order.orderType === 'bespoke_quote' ? 'Bespoke' : humanize(order.cardKind)}</strong>
                  <div className="ent-stack-meta">
                    <span>{formatDate(order.createdAt)}</span>
                    <span>Qty {order.quantity}</span>
                    <span>{humanize(order.paymentStatus)}</span>
                    <span>{humanize(order.designStatus)}</span>
                  </div>
                  <div className="ent-stack-actions">{orderActions(order)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <EntConfirmDialog
        open={!!proofTarget}
        title={proofDecision === 'approved' ? 'Approve this proof?' : 'Request changes?'}
        message={
          proofDecision === 'changes_requested' ? (
            <label className="ent-form-group" style={{ display: 'block' }}>
              Describe what should change
              <textarea value={proofNote} onChange={(e) => setProofNote(e.target.value)} />
            </label>
          ) : 'Approving sends this order into production.'
        }
        detail={proofTarget?.proof?.imageUrl ? 'A proof image is ready for review.' : ''}
        confirmLabel={proofDecision === 'approved' ? 'Approve' : 'Request changes'}
        busy={proofBusy}
        onConfirm={confirmProof}
        onClose={() => !proofBusy && setProofTarget(null)}
      />
    </div>
  );
}
